export type Role = "admin" | "supervisor" | "employee";

// Hierarchy of accounts. Each user has exactly one level. Ranks are
// compared by their numeric order below.
//   owner  — unique; can do anything including transferring ownership.
//   l1     — full edit/view except cannot transfer ownership.
//   l2     — edit/view everything except accounts at or above them.
//   l3     — edit officer status codes only (everything else view-only).
//   l4     — view everything except the audit log.
export type UserLevel = "owner" | "l1" | "l2" | "l3" | "l4";

export interface User {
  id: string;
  agencyId: string;
  username: string;
  email: string;
  passwordHash: string | null;   // null means the account has not accepted the invite yet
  passwordSalt: string | null;
  level: UserLevel;
  // legacy; retained so existing callers don't break during migration
  role: Role;
  employeeId: string | null;
  // Mirrors auth.users.email_confirmed_at via a trigger; non-null
  // once the invitee has clicked the magic link and set a password.
  acceptedAt: string | null;
  disabledAt: string | null;
}

export interface Agency {
  id: string;
  name: string;
  timezone: string;
  weekStart: number;
  minStaffing: number;
  // First day of the schedule grid's 4-week pay period, shared
  // across every user in the agency. Null = no anchor set, so the
  // UI falls back to the pay period containing today. Only owner
  // and L1 can change this via the Schedule page's date controls.
  scheduleAnchorDate: string | null;   // YYYY-MM-DD
  // When true, the schedule UI projects a "tac" view: regular
  // shifts (tacRole = "regular") are hidden, tac shifts are shown,
  // and officers flagged followsTacMode swap to their tacHomeShift
  // + tac days-off pattern. Non-destructive: flipping back off
  // instantly restores the normal view.
  tacModeActive: boolean;
  // Optional "tac mode begins on this date" anchor. When null and
  // tacModeActive is true, every visible day is treated as tac
  // (legacy behaviour). When set, dates before it render as
  // regular and dates on/after it render as tac — so a page that
  // straddles the date shows both shift families side-by-side and
  // the cutover reads as a visible band in the grid. Turning the
  // master flag off immediately reverts every day regardless of
  // this value.
  tacModeStartDate: string | null;
  // Per-agency beta opt-ins. Keys are feature slugs (e.g.
  // "oncall"); value is true when the agency has flipped the
  // feature on. The owner-only gate is enforced in the
  // `useFeatureFlag` hook — flipping the flag alone is necessary
  // but not sufficient during the owner-only beta phase.
  featureFlags: Record<string, boolean>;
}

// A department (Patrol, Dispatch, Animal Control, …). Each department
// has its own roster and its own set of shifts. The Schedule page is
// scoped to one department at a time; the left-nav lists them all.
export interface Department {
  id: string;
  agencyId: string;
  name: string;
  sortOrder: number;
}

export type EmployeeStatus = "active" | "inactive" | "leave";

export interface Employee {
  id: string;
  agencyId: string;
  departmentId: string;
  firstName: string;
  lastName: string;
  badgeNumber: string | null;
  // Radio call sign / unit number printed on the officer's
  // portable. Free-text — agencies number them differently
  // (numeric, "A12", "K9-2", "OIC-1", …). Drives schedule row
  // ordering when the agency picks "radio_rank_badge" seniority
  // mode; falls back to rank+badge for officers who don't carry
  // a radio.
  radioNumber: string | null;
  rank: string | null;
  hireDate: string | null;
  // Last working date. On / after this date the officer drops
  // off the schedule grid and stops counting toward coverage —
  // both regular and tac shifts. Distinct from `status =
  // inactive`, which hides them immediately.
  retirementDate: string | null;
  status: EmployeeStatus;
  isOic: boolean;
  level: UserLevel;
  // Contact channels. First element is "primary" for display purposes;
  // all are saved and any can receive invites.
  emails: string[];
  phones: string[];
  invitedAt: string | null;      // set when a supervisor sent an invite
  homeShiftId: string | null;
  homeUnitId: string | null;
  // Weekly days-off pattern. Two stored patterns (regular + tac)
  // each with a mandatory Week 1 and optional Week 2. When Week 2
  // is null the rotation is single-week (Week 1 repeats every
  // week); otherwise it's a 14-day rotation alternating Week 1 /
  // Week 2 starting from daysOffStartDate. daysOffMode picks which
  // of the two patterns the materialiser writes to the schedule.
  // Weekday indices are 0=Sunday … 6=Saturday (Date.getDay()).
  daysOffWeekdays: number[];              // regular, week 1
  daysOffWeekdaysW2: number[] | null;     // regular, week 2 (null = single-week)
  tacDaysOffWeekdays: number[];           // tac shift, week 1
  tacDaysOffWeekdaysW2: number[] | null;  // tac shift, week 2 (null = single-week)
  daysOffMode: "regular" | "tac";         // which pattern materialises
  daysOffStartDate: string | null;        // YYYY-MM-DD
  // Agency-wide tac mode. When the agency flips tacModeActive on,
  // any officer with followsTacMode = true projects onto their
  // tacHomeShift + tac days-off pattern instead of homeShift +
  // regular days-off. Officers who stay on their regular schedule
  // during tac (e.g. dispatch civilians) leave followsTacMode
  // false.
  followsTacMode: boolean;
  tacHomeShiftId: string | null;
  notes: string | null;
  // Optional headshot / badge photo stored as a data URL. Rendered
  // as a small avatar in lists and expanded in the My profile modal.
  photo: string | null;
  deletedAt: string | null;
}

export interface OvertimeWindow {
  label: string;
  startTime: string;           // HH:MM
  endTime: string;             // HH:MM
}

export interface Shift {
  id: string;
  agencyId: string;
  departmentId: string;
  name: string;
  startTime: string;
  durationMin: number;
  color: string;
  minStaffing: number;
  slotCount: number;           // target number of roster spots (kept for back-compat)
  sortOrder: number;
  // When false, this shift is treated as a specialty role that sits
  // outside the 24/7 coverage rotation: it doesn't demand coverage,
  // its employees aren't picked as OT candidates, and it isn't a
  // neighbor in the prev/next split-OT topology.
  inRotation: boolean;
  // When false, the coverage rollup ignores the OIC axis for this
  // shift (oicRequired = 0). Specialty units / agencies that don't
  // put an Officer-in-Charge on every shift untick this so cells
  // stop short-flagging on OIC alone.
  requiresOic: boolean;
  // Tac-mode visibility for this shift:
  //   "regular" — shown when tac mode is OFF, hidden when ON
  //   "tac"     — shown when tac mode is ON, hidden when OFF
  //   "exempt"  — always shown (Dispatch, Animal Control, …)
  tacRole: "regular" | "tac" | "exempt";
  // Preset OT worked-hours windows supervisors can pick from when
  // an officer from this shift covers OT. Each is the covering
  // officer's actual start / end for the day. An empty list means
  // "always use Custom hours".
  overtimeWindows: OvertimeWindow[];
  // Weekday indices (0 = Sunday … 6 = Saturday) on which this
  // shift doesn't need coverage. School Resource on Sat/Sun, ACO
  // on agency holidays via a future field, etc. The schedule
  // renders the Coverage + OIC cells on these days as N/A
  // instead of red short.
  coverageInactiveWeekdays: number[];
  archivedAt: string | null;
}

// Temporary date-range placement that overrides an officer's
// homeShiftId for a window. Use when an officer is filling in on
// another shift for a stretch of days without permanently changing
// their home shift. Multiple rows can stack; the materialiser
// picks the row with the latest startDate when more than one
// covers the same day.
export interface ShiftAssignment {
  id: string;
  agencyId: string;
  employeeId: string;
  shiftId: string;
  startDate: string;            // YYYY-MM-DD
  endDate: string;              // YYYY-MM-DD, inclusive
  note: string | null;
  createdAt: string;
}

// Point-in-time agency snapshot. Lives in the backups table and
// holds a jsonb dump of every restorable agency-scoped table. The
// list endpoint omits the snapshot blob itself (just metadata) so
// the Settings panel doesn't have to ship megabytes per page.
export interface Backup {
  id: string;
  agencyId: string;
  createdAt: string;
  createdBy: string | null;
  kind: "manual" | "auto";
  label: string | null;
  sizeBytes: number | null;
}

// Per-officer Injury-On-Duty period. Fills cells with the IOD
// note for every date in [startDate, endDate] inclusive. A null
// endDate keeps filling forward until the supervisor sets one or
// clears the row. Layered on top of the days-off pattern: IOD
// takes precedence over a pattern XXX, manual edits still win.
export interface IodPeriod {
  id: string;
  agencyId: string;
  employeeId: string;
  startDate: string;            // YYYY-MM-DD
  endDate: string | null;       // YYYY-MM-DD, inclusive; null = open
  note: string | null;
  setBy: string | null;
  createdAt: string;
}

export type AssignmentStatus =
  | "scheduled" | "on_call" | "working" | "completed" | "cancelled";

export interface Assignment {
  id: string;
  agencyId: string;
  employeeId: string;
  shiftId: string;
  date: string;                // YYYY-MM-DD
  status: AssignmentStatus;
  note: string | null;
  overrideReason: string | null;
  // Groups paired OT assignments (two OT½s filling the same gap,
  // or a single OT Full) so the schedule UI can render them as a
  // single "JJ-CS" chip per coverage event. Null for normal
  // assignments.
  coverageGroupId: string | null;
  // Actual worked-hours window for an OT assignment. Populated by
  // the Fix-coverage modal when the supervisor picks one of the
  // shift's configured windows (or enters custom hours). HH:MM.
  otStartTime: string | null;
  otEndTime: string | null;
  // True when the row was generated by applyDaysOffPattern (auto-
  // materialized XXX from an officer's weekly pattern). Supervisor-
  // typed codes stay false, which is how re-applying the pattern
  // avoids clobbering manual overrides like TR on a normally-off day.
  fromPattern: boolean;
  createdAt: string;
}

export interface ShiftCoverage {
  shiftId: string;
  date: string;
  assigned: number;
  minStaffing: number;
  oicAssigned: number;     // contribution only from employees with isOic = true
  oicRequired: number;     // always 1 for now
  // True when a supervisor has stamped a coverage_overrides row for
  // (shift, date). The schedule grid and Dashboard use this to skip
  // the red-tint / "short" treatment regardless of the bucket math.
  overridden: boolean;
}

// Supervisor-stamped flag that marks a specific (shift, date) cell
// as covered even when the hour-by-hour count comes up short. Built
// for staggered-officer shifts where an edge-hour gap is de-facto
// covered by the adjacent shift's overlap and the supervisor knows
// it doesn't actually need a body.
export interface CoverageOverride {
  id: string;
  agencyId: string;
  shiftId: string;
  date: string;
  reason: string | null;
  setBy: string | null;
  createdAt: string;
}

export type TimeOffType =
  | "vacation" | "sick" | "comp" | "court" | "personal"
  | "military" | "training" | "fmla" | "other";

// Shift-code abbreviation shown on the schedule when an approved
// time-off record covers a cell. Matches the seeded shift_codes
// rows so existing coverage math (coverageValue=0) kicks in
// without any extra work.
export const TIME_OFF_CODE: Record<TimeOffType, string> = {
  vacation: "V",
  sick: "S",
  comp: "CT",
  court: "C",
  personal: "PL",
  military: "ML",
  training: "TR",
  fmla: "F",
  other: "O",
};

export const TIME_OFF_LABEL: Record<TimeOffType, string> = {
  vacation: "Vacation",
  sick: "Sick",
  comp: "Comp time",
  court: "Court",
  personal: "Personal leave",
  military: "Military leave",
  training: "Training",
  fmla: "FMLA",
  other: "Other",
};

// Training requests go through a two-stage approval: an L2
// supervisor partial-approves (status = "l2_approved"), then an
// Owner / L1 finalises (status = "approved"). An Owner / L1 can
// also approve straight from "requested" without the partial
// step. Non-training (time-off) requests skip the partial step
// entirely — any L2+ flips them straight to "approved".
export type TimeOffStatus = "requested" | "l2_approved" | "approved" | "denied";

export interface TimeOff {
  id: string;
  employeeId: string;
  startDate: string;
  endDate: string;
  type: TimeOffType;
  status: TimeOffStatus;
  note: string | null;
  createdAt: string;
  // Agency-specific details captured alongside the request. Training
  // and time-off share the same metadata shape: keys come from the
  // admin-configured request_fields catalogue for the matching scope.
  // Values are strings for text/number and booleans for checkboxes.
  metadata: Record<string, string | number | boolean | null>;
  // Reviewer attribution. For training the L2 partial approval lands
  // in l2ApprovedBy/At and the final Owner/L1 sign-off lands in
  // reviewedBy/At. If L1 approves directly without a partial step,
  // l2ApprovedBy stays null. For non-training only reviewedBy/At
  // populate.
  l2ApprovedBy: string | null;
  l2ApprovedAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
}

// Scope separates training and time-off field catalogues so each
// form has its own list of admin-configured fields. Stored as a
// column on the shared training_fields table.
export type RequestFieldScope = "training" | "time_off";

// Catalogue of custom fields rendered on a request form
// (scope-driven). Admin-editable (Owner / L1). Values live on
// time_off.metadata, keyed by `key`.
export type RequestFieldType = "text" | "number" | "checkbox";

export interface RequestField {
  id: string;
  agencyId: string;
  scope: RequestFieldScope;
  key: string;
  label: string;
  fieldType: RequestFieldType;
  sortOrder: number;
  required: boolean;
  createdAt: string;
}

export interface AuditEntry {
  id: string;
  agencyId: string;
  userId: string | null;
  entityType: string;
  entityId: string;
  action: "create" | "update" | "delete" | "assign" | "unassign" | "login" | "logout";
  // Snapshot of the row before / after the change, stored as JSONB.
  // The client receives the parsed value (object / array / primitive).
  // Null means no snapshot was recorded (e.g. login/logout or a
  // simple action that doesn't carry row state).
  beforeJson: unknown;
  afterJson: unknown;
  createdAt: string;
}

export interface Setting {
  key: string;
  value: string;
}

// Mirrors Stripe's subscription lifecycle states one-to-one. We
// store whatever Stripe sends, no client-side normalisation.
export type SubscriptionStatus =
  | "incomplete" | "incomplete_expired" | "trialing" | "active"
  | "past_due" | "canceled" | "unpaid" | "paused";

export interface Subscription {
  id: string;
  agencyId: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  status: SubscriptionStatus;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEnd: string | null;
  priceId: string | null;
  createdAt: string;
  updatedAt: string;
}

// A code that supervisors can drop into a schedule cell to mark a day off,
// a partial day, or any other non-default status. Editable from Settings.
//   coverageValue: contribution to the shift's required-staffing count.
//     0   = the officer is not working at all (XXX, S, PL, SA, V, IOD, …)
//     0.5 = partial coverage (e.g. half-day comp, court for half the shift)
//     1   = the officer is working (uncommon for codes; empty cell already
//           means "working")
// Per-agency catalogue of on-call rotations (Detectives, SWAT, K-9,
// Animal Control, …). Lives entirely outside the shift / coverage
// system; rosters here have no bearing on shift coverage math, OT
// picking, or tac mode. Each row is one rotation; assignments live
// in OnCallAssignment.
export interface OnCallRoster {
  id: string;
  agencyId: string;
  name: string;
  // Hex color tint for the strip + month-view chrome. Defaults to
  // slate-500 so on-call doesn't visually compete with shift colors.
  color: string;
  // When true, an assignment may be created with role='secondary'
  // in addition to 'primary'. Per-roster toggle.
  hasSecondary: boolean;
  // Optional contact channels surfaced next to the roster name.
  radioChannel: string | null;
  contactPhone: string | null;
  sortOrder: number;
  archivedAt: string | null;
  createdAt: string;
}

// Whole-day inclusive on-call assignment. Multiple rows may overlap
// a single date when a roster supports secondaries (one primary +
// one secondary per date, typically).
export interface OnCallAssignment {
  id: string;
  agencyId: string;
  rosterId: string;
  employeeId: string;
  startDate: string;            // YYYY-MM-DD
  endDate: string;              // YYYY-MM-DD, inclusive
  role: "primary" | "secondary";
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface ShiftCode {
  id: string;
  agencyId: string;
  code: string;          // short text shown in cell, e.g. "XXX", "S", "PL"
  label: string;         // human-readable meaning, e.g. "Scheduled off"
  coverageValue: number; // 0 | 0.5 | 1
  color: string | null;  // optional cell tint; null = use shift colour
  sortOrder: number;
}

// One row per (user, pending request) — Apple Guideline 5.1.1(v)
// account-deletion flow. Officers submit; supervisors resolve.
export interface DeletionRequest {
  id: string;
  userId: string;
  agencyId: string;
  requestedAt: string;
  reason: string | null;
  status: "pending" | "approved" | "denied" | "canceled";
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolvedNote: string | null;
}

// One row per (user, notification) — written by the send-notification
// edge function and surfaced in the bell-icon inbox.
export interface NotificationItem {
  id: string;
  subject: string;
  body: string;
  kind: string;
  emailed: boolean;
  pushed: boolean;
  readAt: string | null;
  createdAt: string;
}
